import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  NativeSelect,
  Skeleton,
  Stack,
  Switch,
  Table,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useActivateReasonCode,
  useCreateReasonCode,
  useDeactivateReasonCode,
  useReasonCodes,
  useUpdateReasonCode,
  type ReasonCode,
} from '../api/reason-codes';
import { useOrgs } from '../api/orgs';

type Scope = 'halt' | 'step_failure' | 'human_decline';

const SCOPE_LABELS: Record<Scope, string> = {
  halt: 'Halt',
  step_failure: 'Step Failure',
  human_decline: 'Human Decline',
};

function parseApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.body) as { message?: string };
      if (parsed.message) return parsed.message;
    } catch {
      /* fallthrough */
    }
  }
  return fallback;
}

interface ReasonCodeRowProps {
  code: ReasonCode;
  orgId: string | null;
  onEdit: (code: ReasonCode) => void;
  onDeactivate: (code: ReasonCode) => void;
  onActivate: (code: ReasonCode) => void;
}

function ReasonCodeRow({ code, orgId, onEdit, onDeactivate, onActivate }: ReasonCodeRowProps) {
  return (
    <Table.Tr style={{ opacity: code.active ? 1 : 0.5 }}>
      <Table.Td>
        <Text size="sm" ff="monospace">
          {code.code}
        </Text>
      </Table.Td>
      <Table.Td>{code.label}</Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {code.description ?? '—'}
        </Text>
      </Table.Td>
      <Table.Td>{code.requiresNote ? 'Yes' : 'No'}</Table.Td>
      <Table.Td>
        <Group gap={4}>
          <Badge color={code.source === 'system' ? 'gray' : 'blue'} variant="light">
            {code.source}
          </Badge>
          {!code.active && (
            <Badge color="gray" variant="outline">
              inactive
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          {code.active && (
            <Button size="xs" variant="subtle" onClick={() => onEdit(code)}>
              Edit
            </Button>
          )}
          {code.source === 'org' && code.active && orgId !== null && (
            <Button size="xs" variant="subtle" color="red" onClick={() => onDeactivate(code)}>
              Deactivate
            </Button>
          )}
          {code.source === 'org' && !code.active && orgId !== null && (
            <Button size="xs" variant="subtle" color="green" onClick={() => onActivate(code)}>
              Activate
            </Button>
          )}
        </Group>
      </Table.Td>
    </Table.Tr>
  );
}

interface ReasonCodesTableProps {
  scope: Scope;
  orgId: string | null;
  onEdit: (code: ReasonCode) => void;
  onDeactivate: (code: ReasonCode) => void;
  onActivate: (code: ReasonCode) => void;
}

function ReasonCodesTable({
  scope,
  orgId,
  onEdit,
  onDeactivate,
  onActivate,
}: ReasonCodesTableProps) {
  const query = useReasonCodes({ scope, includeInactive: true, ...(orgId ? { orgId } : {}) });

  if (query.isPending) {
    return (
      <Stack gap="xs">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={36} />
        ))}
      </Stack>
    );
  }

  if (query.isError) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />}>
        Failed to load reason codes.
      </Alert>
    );
  }

  if (!query.data?.length) {
    return (
      <Text c="dimmed" size="sm">
        No codes for this scope.
      </Text>
    );
  }

  return (
    <Table withTableBorder highlightOnHover>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Code</Table.Th>
          <Table.Th>Label</Table.Th>
          <Table.Th>Description</Table.Th>
          <Table.Th>Requires Note</Table.Th>
          <Table.Th>Source</Table.Th>
          <Table.Th>Actions</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {query.data.map((rc) => (
          <ReasonCodeRow
            key={rc.id}
            code={rc}
            orgId={orgId}
            onEdit={onEdit}
            onDeactivate={onDeactivate}
            onActivate={onActivate}
          />
        ))}
      </Table.Tbody>
    </Table>
  );
}

export function ReasonCodesPage() {
  const { orgId = null } = useParams<{ orgId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeScope = (searchParams.get('scope') as Scope | null) ?? 'halt';
  const navigate = useNavigate();

  const orgsQuery = useOrgs();

  // ─── Edit modal ─────────────────────────────────────────────────────────────
  const [editTarget, setEditTarget] = useState<ReasonCode | null>(null);
  const [editOpen, { open: openEdit, close: closeEdit }] = useDisclosure(false);
  const [editLabel, setEditLabel] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editRequiresNote, setEditRequiresNote] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const updateMutation = useUpdateReasonCode(editTarget?.id ?? '');

  function handleOpenEdit(code: ReasonCode) {
    setEditTarget(code);
    setEditLabel(code.label);
    setEditDescription(code.description ?? '');
    setEditRequiresNote(code.requiresNote);
    setEditError(null);
    openEdit();
  }

  function handleCloseEdit() {
    setEditTarget(null);
    setEditError(null);
    closeEdit();
  }

  function handleSubmitEdit(e: React.FormEvent) {
    e.preventDefault();
    setEditError(null);
    if (!editLabel.trim()) {
      setEditError('Label is required.');
      return;
    }
    updateMutation.mutate(
      {
        label: editLabel.trim(),
        description: editDescription.trim() || undefined,
        requiresNote: editRequiresNote,
      },
      {
        onSuccess: handleCloseEdit,
        onError: (err) => setEditError(parseApiError(err, 'Failed to update.')),
      },
    );
  }

  // ─── Create modal (org view only) ───────────────────────────────────────────
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [createCode, setCreateCode] = useState('');
  const [createLabel, setCreateLabel] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createRequiresNote, setCreateRequiresNote] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const createMutation = useCreateReasonCode();

  function handleCloseCreate() {
    setCreateCode('');
    setCreateLabel('');
    setCreateDescription('');
    setCreateRequiresNote(false);
    setCreateError(null);
    closeCreate();
  }

  function handleSubmitCreate(e: React.FormEvent) {
    e.preventDefault();
    setCreateError(null);
    if (!createCode.trim()) {
      setCreateError('Code is required.');
      return;
    }
    if (!createLabel.trim()) {
      setCreateError('Label is required.');
      return;
    }
    createMutation.mutate(
      {
        scope: activeScope,
        ...(orgId ? { org_id: orgId } : {}),
        code: createCode.trim(),
        label: createLabel.trim(),
        description: createDescription.trim() || undefined,
        requiresNote: createRequiresNote,
      },
      {
        onSuccess: handleCloseCreate,
        onError: (err) => setCreateError(parseApiError(err, 'Failed to create.')),
      },
    );
  }

  // ─── Deactivate modal ───────────────────────────────────────────────────────
  const [deactivateTarget, setDeactivateTarget] = useState<ReasonCode | null>(null);
  const [deactivateOpen, { open: openDeactivate, close: closeDeactivate }] = useDisclosure(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  const deactivateMutation = useDeactivateReasonCode(deactivateTarget?.id ?? '');

  function handleOpenDeactivate(code: ReasonCode) {
    setDeactivateTarget(code);
    setDeactivateError(null);
    openDeactivate();
  }

  function handleCloseDeactivate() {
    setDeactivateTarget(null);
    setDeactivateError(null);
    closeDeactivate();
  }

  function handleConfirmDeactivate() {
    deactivateMutation.mutate(undefined, {
      onSuccess: handleCloseDeactivate,
      onError: (err) => setDeactivateError(parseApiError(err, 'Failed to deactivate.')),
    });
  }

  // ─── Activate modal ─────────────────────────────────────────────────────────
  const [activateTarget, setActivateTarget] = useState<ReasonCode | null>(null);
  const [activateOpen, { open: openActivate, close: closeActivate }] = useDisclosure(false);
  const [activateError, setActivateError] = useState<string | null>(null);

  const activateMutation = useActivateReasonCode(activateTarget?.id ?? '');

  function handleOpenActivate(code: ReasonCode) {
    setActivateTarget(code);
    setActivateError(null);
    openActivate();
  }

  function handleCloseActivate() {
    setActivateTarget(null);
    setActivateError(null);
    closeActivate();
  }

  function handleConfirmActivate() {
    activateMutation.mutate(undefined, {
      onSuccess: handleCloseActivate,
      onError: (err) => setActivateError(parseApiError(err, 'Failed to activate.')),
    });
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>Reason Codes</Title>
        <Group gap="sm" align="flex-end">
          <NativeSelect
            label="Organisation"
            value={orgId ?? ''}
            onChange={(e) => {
              const v = e.currentTarget.value;
              navigate(v ? `/orgs/${v}/reason-codes` : '/reason-codes');
            }}
            data={[
              { value: '', label: 'System (all orgs)' },
              ...(orgsQuery.data ?? []).map((o) => ({ value: o.id, label: o.name })),
            ]}
            w={220}
          />
          {orgId && <Button onClick={openCreate}>Add reason code</Button>}
        </Group>
      </Group>

      <Tabs value={activeScope} onChange={(v) => setSearchParams({ scope: v ?? 'halt' })}>
        <Tabs.List>
          {(Object.keys(SCOPE_LABELS) as Scope[]).map((scope) => (
            <Tabs.Tab key={scope} value={scope}>
              {SCOPE_LABELS[scope]}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {(Object.keys(SCOPE_LABELS) as Scope[]).map((scope) => (
          <Tabs.Panel key={scope} value={scope} pt="md">
            <ReasonCodesTable
              scope={scope}
              orgId={orgId}
              onEdit={handleOpenEdit}
              onDeactivate={handleOpenDeactivate}
              onActivate={handleOpenActivate}
            />
          </Tabs.Panel>
        ))}
      </Tabs>

      {/* Edit modal */}
      <Modal
        opened={editOpen}
        onClose={handleCloseEdit}
        title={`Edit: ${editTarget?.code}`}
        transitionProps={{ duration: 0 }}
      >
        <Stack component="form" gap="md" onSubmit={handleSubmitEdit}>
          <TextInput
            label="Label"
            required
            value={editLabel}
            onChange={(e) => setEditLabel(e.currentTarget.value)}
            disabled={updateMutation.isPending}
          />
          <Textarea
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.currentTarget.value)}
            disabled={updateMutation.isPending}
          />
          <Switch
            label="Requires note"
            checked={editRequiresNote}
            onChange={(e) => setEditRequiresNote(e.currentTarget.checked)}
            disabled={updateMutation.isPending}
          />
          {editError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {editError}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={handleCloseEdit} disabled={updateMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={updateMutation.isPending}>
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Create modal (org view only) */}
      <Modal
        opened={createOpen}
        onClose={handleCloseCreate}
        title={`Add reason code — ${SCOPE_LABELS[activeScope]}`}
        transitionProps={{ duration: 0 }}
      >
        <Stack component="form" gap="md" onSubmit={handleSubmitCreate}>
          <TextInput
            label="Code"
            description="Machine-readable identifier (e.g. custom_hold). Cannot be changed after creation."
            required
            value={createCode}
            onChange={(e) => setCreateCode(e.currentTarget.value)}
            disabled={createMutation.isPending}
          />
          <TextInput
            label="Label"
            required
            value={createLabel}
            onChange={(e) => setCreateLabel(e.currentTarget.value)}
            disabled={createMutation.isPending}
          />
          <Textarea
            label="Description"
            value={createDescription}
            onChange={(e) => setCreateDescription(e.currentTarget.value)}
            disabled={createMutation.isPending}
          />
          <Switch
            label="Requires note"
            checked={createRequiresNote}
            onChange={(e) => setCreateRequiresNote(e.currentTarget.checked)}
            disabled={createMutation.isPending}
          />
          {createError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {createError}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={handleCloseCreate}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" loading={createMutation.isPending}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Deactivate confirmation modal */}
      <Modal
        opened={deactivateOpen}
        onClose={handleCloseDeactivate}
        title="Deactivate reason code?"
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <Text size="sm">
            <strong>{deactivateTarget?.code}</strong> will be deactivated for this organisation. It
            will no longer appear in the workflow editor.
          </Text>
          {deactivateError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {deactivateError}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={handleCloseDeactivate}
              disabled={deactivateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleConfirmDeactivate}
              loading={deactivateMutation.isPending}
            >
              Deactivate
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Activate confirmation modal */}
      <Modal
        opened={activateOpen}
        onClose={handleCloseActivate}
        title="Activate reason code?"
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <Text size="sm">
            <strong>{activateTarget?.code}</strong> will be re-activated for this organisation and
            will appear in the workflow editor again.
          </Text>
          {activateError && (
            <Alert color="red" icon={<IconAlertTriangle size={18} />}>
              {activateError}
            </Alert>
          )}
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={handleCloseActivate}
              disabled={activateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleConfirmActivate}
              loading={activateMutation.isPending}
            >
              Activate
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
